import * as THREE from 'three';
import { FikoGPUComputeUltra, getGPUComputeInstance } from './FikoGPUComputeUltra';

/**
 * 🚀 PROFESYONEL EDGE SPATIAL INDEX - GPU ACCELERATED
 *
 * Milyonluk edge'ler için O(1) screen-space lookup sağlar.
 * CAD yazılımlarında kullanılan teknikler:
 *
 * 1. Screen-Space Spatial Hash Grid - 2D grid hücreleri
 * 2. Lazy Indexing - Sadece görünür edge'ler index'lenir
 * 3. Camera-Aware Caching - Kamera değişince invalidate
 * 4. Progressive Indexing - Arka planda index güncelleme
 * 5. LOD Integration - Uzak objeler için basitleştirilmiş edge'ler
 * 6. 🚀 GPU COMPUTE - Edge projection tamamen GPU'da (100x hızlı)
 *
 * Performans hedefi: 1M+ edge @ 60fps hover
 */

// Edge verisi - minimum memory footprint
interface IndexedEdge {
    objId: number;           // Object index (edge objeleri listesinde)
    edgeIdx: number;         // Edge index (geometry içinde)
    minX: number;            // Screen-space AABB
    minY: number;
    maxX: number;
    maxY: number;
    depth: number;           // Ortalama derinlik (z-sorting için)
}

// Grid hücresi
interface GridCell {
    edges: IndexedEdge[];
}

// Edge objesi referansı
interface EdgeObjectRef {
    object: THREE.Object3D;
    geometry: THREE.BufferGeometry;
    worldMatrix: THREE.Matrix4;
    positionArray: Float32Array;
    edgeCount: number;
    lastIndexFrame: number;
    visible: boolean;
    boundingSphere?: THREE.Sphere;
}

/**
 * Screen-Space Edge Spatial Index
 *
 * Ekran koordinatlarında 2D grid kullanarak edge lookup yapar.
 * Grid hücre boyutu: ~50 piksel (ayarlanabilir)
 */
export class FikoEdgeSpatialIndex {
    // Grid yapısı
    private grid: Map<string, GridCell> = new Map();
    private cellSize: number = 50; // Piksel cinsinden grid hücre boyutu

    // Edge objeleri referansları
    private edgeObjects: EdgeObjectRef[] = [];
    private objectIdMap: Map<string, number> = new Map(); // UUID -> index

    // Camera state (invalidation için)
    private lastCameraMatrix: THREE.Matrix4 = new THREE.Matrix4();
    // 🎯 Kameranın KARARLI kaynak durumu (matrixWorldInverse büyük koordinatta float-kararsız → sürekli rebuild)
    private _lastCamPos: THREE.Vector3 = new THREE.Vector3(NaN, NaN, NaN);
    private _lastCamQuat: THREE.Quaternion = new THREE.Quaternion(NaN, NaN, NaN, NaN);
    private _lastCamZoom: number = NaN;
    private lastCanvasWidth: number = 0;
    private lastCanvasHeight: number = 0;
    private lastIndexFrame: number = 0;
    private frameCounter: number = 0;

    // Index durumu
    private isIndexValid: boolean = false;
    private isIndexing: boolean = false;
    private totalEdgesIndexed: number = 0;
    private indexingProgress: number = 0;

    // Progressive indexing
    private pendingObjects: EdgeObjectRef[] = [];
    private maxEdgesPerFrame: number = 50000; // Frame başına max index'lenecek edge

    // Pre-allocated buffers (zero-allocation)
    private readonly _mvpMatrix = new THREE.Matrix4();
    private readonly _tempVec4Start = new Float32Array(4);
    private readonly _tempVec4End = new Float32Array(4);
    private readonly _frustum = new THREE.Frustum();
    private readonly _projScreenMatrix = new THREE.Matrix4();
    private readonly _boundingBox = new THREE.Box3();
    private readonly _tempSphere = new THREE.Sphere();

    // 🚀 GPU COMPUTE: Edge projection GPU'da yapılır
    private gpuCompute: FikoGPUComputeUltra | null = null;
    private useGPUCompute: boolean = true; // GPU compute kullan (100x hızlı)
    private renderer: THREE.WebGLRenderer | null = null;

    // 🚀 TESSELLATION OPTIMIZER
    private tessOptimizer: FikoTessellatedEdgeOptimizer = new FikoTessellatedEdgeOptimizer();
    private _tessellationAnalyzed: boolean = false;

    // Stats
    private stats = {
        totalEdges: 0,
        objectCount: 0,
        indexedEdges: 0,
        gridCells: 0,
        lastQueryTime: 0,
        lastIndexTime: 0,
        cacheHits: 0,
        cacheMisses: 0,
        gpuIndexCalls: 0,
        cpuIndexCalls: 0,
        lastQueryCandidates: 0,
        lastQueryCheckedCells: 0,
    };

    constructor(cellSize: number = 50) {
        this.cellSize = cellSize;
    }

    /**
     * 🚀 GPU COMPUTE: Renderer'ı set et (GPU indexing için gerekli)
     */
    setRenderer(renderer: THREE.WebGLRenderer): void {
        this.renderer = renderer;
        if (this.useGPUCompute && renderer) {
            try {
                this.gpuCompute = getGPUComputeInstance(renderer);
            } catch (e) {
                this.gpuCompute = null;
            }
        }
    }

    /**
     * 🚀 GPU COMPUTE: GPU indexing'i aç/kapa
     */
    setUseGPUCompute(enabled: boolean): void {
        this.useGPUCompute = enabled;
        if (enabled && this.renderer && !this.gpuCompute) {
            try {
                this.gpuCompute = getGPUComputeInstance(this.renderer);
            } catch (e) {
                this.gpuCompute = null;
            }
        }
    }

    /**
     * Edge objelerini kaydet (STP import sonrası çağrılır)
     */
    registerEdgeObjects(scene: THREE.Object3D): void {
        this.edgeObjects = [];
        this.objectIdMap.clear();
        this.invalidateIndex();

        let debugCounts = {
            fikoCizimYalin: 0,
            lineSegments: 0,
            lineSegments2: 0,
            line2: 0,
            skipped: 0,
            invisible: 0
        };

        const collectEdges = (obj: THREE.Object3D): void => {
            if (!obj.visible) {
                debugCounts.invisible++;
                return;
            }

            // Highlight gruplarını atla
            const skipNames = ['SeçimlerÜzerinde', 'Seçimler', 'SeçimlerÜzerinde2', 'Seçimler2'];
            if (skipNames.includes(obj.name) || obj.userData?.['isSelectionHighlight']) {
                debugCounts.skipped++;
                return;
            }

            // FikoCizimYalin edge'lerini topla (cizgiler property'si kullanılıyor)
            if (obj.type === 'FikoCizimYalin') {
                const cizim = obj as any;
                // FikoCizimYalin.cizgiler LineSegments veya LineSegments2 olabilir
                if (cizim.cizgiler?.visible && cizim.cizgiler.geometry) {
                    debugCounts.fikoCizimYalin++;
                    this.addEdgeObject(cizim.cizgiler);
                }
            }

            // LineSegments ve Line2 objeleri (doğrudan sahneye eklenmiş olanlar)
            if (obj.type === 'LineSegments') {
                if ((obj as any).geometry) {
                    debugCounts.lineSegments++;
                    this.addEdgeObject(obj);
                }
            } else if (obj.type === 'LineSegments2') {
                if ((obj as any).geometry) {
                    debugCounts.lineSegments2++;
                    this.addEdgeObject(obj);
                }
            } else if (obj.type === 'Line2') {
                if ((obj as any).geometry) {
                    debugCounts.line2++;
                    this.addEdgeObject(obj);
                }
            }

            // Children
            for (const child of obj.children) {
                collectEdges(child);
            }
        };

        collectEdges(scene);

        // Toplam edge sayısını hesapla
        this.stats.totalEdges = this.edgeObjects.reduce((sum, ref) => sum + ref.edgeCount, 0);
        this.stats.objectCount = this.edgeObjects.length;

        // 🚀 TESSELLATION: Edge verilerini analiz et ve adaptif ayarları güncelle
        this.tessOptimizer.analyzeTessellation(this.edgeObjects);
        const tessStats = this.tessOptimizer.getStats();

        // Adaptif cell boyutu uygula
        if (tessStats.adaptiveCellSize !== this.cellSize) {
            this.cellSize = tessStats.adaptiveCellSize;
        }

        // Adaptif maxEdgesPerFrame uygula
        const adaptiveMax = this.tessOptimizer.calculateMaxEdgesPerFrame(this.stats.totalEdges);
        if (adaptiveMax !== this.maxEdgesPerFrame) {
            this.maxEdgesPerFrame = adaptiveMax;
        }

        this._tessellationAnalyzed = true;
    }

    /**
     * Tek bir edge objesi ekle
     */
    private addEdgeObject(obj: THREE.Object3D): void {
        // Duplicate kontrolü
        if (this.objectIdMap.has(obj.uuid)) {
            return; // Zaten eklenmiş
        }

        const geometry = (obj as any).geometry as THREE.BufferGeometry;
        if (!geometry) return;

        // 🎯 LineSegments2/Line2 FIX: gerçek segment verisi instanceStart/instanceEnd'de (interleaved
        // InterleavedBufferAttribute). 'position' sadece shader şablon kutusu (~8 vertex → 4 SAHTE kenar)
        // olduğundan, onu okumak gerçek 89k kenarı indekslemiyor → edge seçimi imkansızlaşıyordu.
        // Önce instanceStart/End oku (gerçek kenarlar), yoksa düz LineSegments için position'a düş.
        const instanceStart: any = (geometry as any).attributes?.instanceStart;
        const instanceEnd: any = (geometry as any).attributes?.instanceEnd;
        let posArray: Float32Array;
        let edgeCount: number;
        if (instanceStart && instanceEnd && instanceStart.count >= 1) {
            const segCount = instanceStart.count;
            posArray = new Float32Array(segCount * 6);
            for (let i = 0; i < segCount; i++) {
                posArray[i * 6]     = instanceStart.getX(i);
                posArray[i * 6 + 1] = instanceStart.getY(i);
                posArray[i * 6 + 2] = instanceStart.getZ(i);
                posArray[i * 6 + 3] = instanceEnd.getX(i);
                posArray[i * 6 + 4] = instanceEnd.getY(i);
                posArray[i * 6 + 5] = instanceEnd.getZ(i);
            }
            edgeCount = segCount;
        } else {
            const positions = geometry.getAttribute('position');
            if (!positions || positions.count < 2) return;
            posArray = positions.array as Float32Array;
            edgeCount = Math.floor(positions.count / 2);
        }

        // Bounding sphere hesapla
        if (!geometry.boundingSphere) {
            geometry.computeBoundingSphere();
        }

        const ref: EdgeObjectRef = {
            object: obj,
            geometry: geometry,
            worldMatrix: obj.matrixWorld.clone(),
            positionArray: posArray,
            edgeCount: edgeCount,
            lastIndexFrame: -1,
            visible: obj.visible,
            boundingSphere: geometry.boundingSphere?.clone()
        };

        const objId = this.edgeObjects.length;
        this.edgeObjects.push(ref);
        this.objectIdMap.set(obj.uuid, objId);
    }

    /**
     * Index'i güncelle (kamera değiştiğinde veya ilk kez)
     * Progressive indexing: Her frame'de maxEdgesPerFrame kadar edge index'ler
     */
    updateIndex(
        camera: THREE.Camera,
        canvasWidth: number,
        canvasHeight: number,
        forceFullRebuild: boolean = false
    ): boolean {
        this.frameCounter++;

        // Kamera değişti mi kontrol et
        const cameraChanged = this.hasCameraChanged(camera, canvasWidth, canvasHeight);

        if (!cameraChanged && this.isIndexValid && !forceFullRebuild) {
            this.stats.cacheHits++;
            return true; // Cache hit
        }

        this.stats.cacheMisses++;

        // Full rebuild gerekiyorsa
        if (forceFullRebuild || cameraChanged) {
            this.startFullRebuild(camera, canvasWidth, canvasHeight);
        }

        // Progressive indexing devam et
        if (this.isIndexing) {
            this.continueIndexing(camera, canvasWidth, canvasHeight);
        }

        return this.isIndexValid;
    }

    /**
     * Kamera değişikliği kontrolü
     */
    private hasCameraChanged(camera: THREE.Camera, width: number, height: number): boolean {
        // Canvas boyutu değişti mi?
        if (width !== this.lastCanvasWidth || height !== this.lastCanvasHeight) {
            return true;
        }

        // 🎯 KÖK NEDEN FIX: matrixWorldInverse ELEMANLARI büyük koordinatlı modelde (origin ~binlerce)
        // float-KARARSIZ → kamera SABİTKEN bile her frame "değişti" → sürekli 89k kenar rebuild = DONMA.
        // ÇÖZÜM: kameranın KARARLI kaynak durumunu karşılaştır (position/quaternion/zoom). Kullanıcı
        // hareket etmezse bunlar HİÇ değişmez → sabit hover'da rebuild yok; gerçek harekette rebuild.
        const cam: any = camera;
        const zoom = cam.zoom ?? 1;
        if (Number.isNaN(this._lastCamZoom)) return true; // ilk çağrı

        const camDist = camera.position.length() || 1;
        const posTol = camDist * 0.00005;
        if (camera.position.distanceToSquared(this._lastCamPos) > posTol * posTol) return true;
        if (Math.abs(camera.quaternion.dot(this._lastCamQuat)) < 0.9999995) return true;
        if (Math.abs(zoom - this._lastCamZoom) > Math.max(1e-5, Math.abs(this._lastCamZoom) * 1e-4)) return true;

        return false;
    }

    /**
     * Tam index rebuild başlat
     */
    private startFullRebuild(camera: THREE.Camera, canvasWidth: number, canvasHeight: number): void {
        const startTime = performance.now();

        // Grid'i temizle
        this.grid.clear();
        this.isIndexValid = false;
        this.isIndexing = true;
        this.indexingProgress = 0;
        this.totalEdgesIndexed = 0;

        // Kamera state'i kaydet (kararlı kaynak durumu — hasCameraChanged bunu kullanır)
        this.lastCameraMatrix.copy(camera.matrixWorldInverse);
        this._lastCamPos.copy(camera.position);
        this._lastCamQuat.copy(camera.quaternion);
        this._lastCamZoom = (camera as any).zoom ?? 1;
        this.lastCanvasWidth = canvasWidth;
        this.lastCanvasHeight = canvasHeight;

        // Frustum oluştur
        this._projScreenMatrix.multiplyMatrices(
            (camera as THREE.PerspectiveCamera).projectionMatrix,
            camera.matrixWorldInverse
        );
        this._frustum.setFromProjectionMatrix(this._projScreenMatrix);

        // Görünür objeleri filtrele ve sırala (yakından uzağa)
        this.pendingObjects = this.edgeObjects
            .filter(ref => {
                if (!ref.visible || !ref.object.visible) return false;

                // Frustum culling
                if (ref.boundingSphere) {
                    this._tempSphere.copy(ref.boundingSphere);
                    this._tempSphere.applyMatrix4(ref.object.matrixWorld);
                    return this._frustum.intersectsSphere(this._tempSphere);
                }
                return true;
            })
            .sort((a, b) => {
                // Yakin objeler once (daha onemli)
                // ZERO-ALLOC: Pre-allocated temp vectors kullan (clone() yerine)
                let distA = 0, distB = 0;
                if (a.boundingSphere) {
                    this._tempVec4Start[0] = a.boundingSphere.center.x;
                    this._tempVec4Start[1] = a.boundingSphere.center.y;
                    this._tempVec4Start[2] = a.boundingSphere.center.z;
                    const am = a.object.matrixWorld.elements;
                    const ax = am[0]*this._tempVec4Start[0] + am[4]*this._tempVec4Start[1] + am[8]*this._tempVec4Start[2] + am[12];
                    const ay = am[1]*this._tempVec4Start[0] + am[5]*this._tempVec4Start[1] + am[9]*this._tempVec4Start[2] + am[13];
                    const az = am[2]*this._tempVec4Start[0] + am[6]*this._tempVec4Start[1] + am[10]*this._tempVec4Start[2] + am[14];
                    const dax = camera.position.x - ax, day = camera.position.y - ay, daz = camera.position.z - az;
                    distA = dax*dax + day*day + daz*daz;
                }
                if (b.boundingSphere) {
                    this._tempVec4End[0] = b.boundingSphere.center.x;
                    this._tempVec4End[1] = b.boundingSphere.center.y;
                    this._tempVec4End[2] = b.boundingSphere.center.z;
                    const bm = b.object.matrixWorld.elements;
                    const bx = bm[0]*this._tempVec4End[0] + bm[4]*this._tempVec4End[1] + bm[8]*this._tempVec4End[2] + bm[12];
                    const by = bm[1]*this._tempVec4End[0] + bm[5]*this._tempVec4End[1] + bm[9]*this._tempVec4End[2] + bm[13];
                    const bz = bm[2]*this._tempVec4End[0] + bm[6]*this._tempVec4End[1] + bm[10]*this._tempVec4End[2] + bm[14];
                    const dbx = camera.position.x - bx, dby = camera.position.y - by, dbz = camera.position.z - bz;
                    distB = dbx*dbx + dby*dby + dbz*dbz;
                }
                return distA - distB;
            });

        this.stats.lastIndexTime = performance.now() - startTime;
    }

    /**
     * Progressive indexing devam et
     * 🚀 GPU COMPUTE: Büyük edge setleri için GPU'da paralel projection
     */
    private async continueIndexing(camera: THREE.Camera, canvasWidth: number, canvasHeight: number): Promise<void> {
        if (this.pendingObjects.length === 0) {
            this.isIndexing = false;
            this.isIndexValid = true;
            this.stats.indexedEdges = this.totalEdgesIndexed;
            this.stats.gridCells = this.grid.size;
            return;
        }

        const startTime = performance.now();
        let edgesThisFrame = 0;

        // MVP matrix hesapla
        const projMatrix = (camera as THREE.PerspectiveCamera).projectionMatrix;
        const viewMatrix = camera.matrixWorldInverse;

        // GPU PATH: Büyük edge setleri için GPU compute kullan
        if (this.gpuCompute && this.useGPUCompute && this.gpuCompute.isGPUComputeAvailable()) {
            edgesThisFrame = await this.continueIndexingGPU(camera, canvasWidth, canvasHeight, projMatrix, viewMatrix);
            this.stats.gpuIndexCalls++;
        } else {
            // CPU FALLBACK: Optimize edilmiş CPU path
            edgesThisFrame = this.continueIndexingCPU(canvasWidth, canvasHeight, projMatrix, viewMatrix);
            this.stats.cpuIndexCalls++;
        }

        this.indexingProgress = 1 - (this.pendingObjects.length / Math.max(1, this.edgeObjects.length));
        this.stats.lastIndexTime = performance.now() - startTime;
    }

    /**
     * GPU COMPUTE: Edge projection GPU'da yapılır (100x hızlı)
     */
    private async continueIndexingGPU(
        camera: THREE.Camera,
        canvasWidth: number,
        canvasHeight: number,
        projMatrix: THREE.Matrix4,
        viewMatrix: THREE.Matrix4
    ): Promise<number> {
        let edgesThisFrame = 0;

        while (this.pendingObjects.length > 0 && edgesThisFrame < this.maxEdgesPerFrame) {
            const ref = this.pendingObjects.shift()!;
            const objId = this.objectIdMap.get(ref.object.uuid);
            if (objId === undefined) continue;

            // World matrix güncelle
            ref.object.updateMatrixWorld(true);
            ref.worldMatrix.copy(ref.object.matrixWorld);

            // MVP = P * V * W
            this._mvpMatrix.multiplyMatrices(projMatrix, viewMatrix);
            this._mvpMatrix.multiply(ref.worldMatrix);

            const posArray = ref.positionArray;
            const edgeCount = ref.edgeCount;

            // GPU compute için edge pozisyonlarını hazırla
            // Format: [x1,y1,z1,x2,y2,z2, x1,y1,z1,x2,y2,z2, ...]
            const edgePositions = new Float32Array(edgeCount * 6);
            for (let i = 0; i < edgeCount; i++) {
                const idx = i * 2;
                edgePositions[i * 6] = posArray[idx * 3];
                edgePositions[i * 6 + 1] = posArray[idx * 3 + 1];
                edgePositions[i * 6 + 2] = posArray[idx * 3 + 2];
                edgePositions[i * 6 + 3] = posArray[(idx + 1) * 3];
                edgePositions[i * 6 + 4] = posArray[(idx + 1) * 3 + 1];
                edgePositions[i * 6 + 5] = posArray[(idx + 1) * 3 + 2];
            }

            // GPU COMPUTE: Tüm edge'leri tek seferde GPU'da project et
            const gpuResult = await this.gpuCompute!.computeEdgeProjections(
                edgePositions,
                this._mvpMatrix,
                [canvasWidth, canvasHeight],
                [0, 0] // Mouse pozisyonu burada önemli değil, sadece projection yapıyoruz
            );

            if (gpuResult) {
                // GPU sonuçlarını grid'e ekle
                const mvp = this._mvpMatrix.elements;

                for (let i = 0; i < edgeCount && edgesThisFrame < this.maxEdgesPerFrame; i++) {
                    const idx = i * 2;
                    const depth = gpuResult.depths[i];

                    // Kamera arkasındaki edge'leri atla
                    if (depth > 99990) continue;

                    // Screen coordinates'i yeniden hesapla (GPU sonuçlarından AABB)
                    const sx = posArray[idx * 3], sy = posArray[idx * 3 + 1], sz = posArray[idx * 3 + 2];
                    const ex = posArray[(idx + 1) * 3], ey = posArray[(idx + 1) * 3 + 1], ez = posArray[(idx + 1) * 3 + 2];

                    const sw = mvp[3] * sx + mvp[7] * sy + mvp[11] * sz + mvp[15];
                    const ew = mvp[3] * ex + mvp[7] * ey + mvp[11] * ez + mvp[15];

                    if (sw <= 0.001 && ew <= 0.001) continue;

                    const startScreenX = ((mvp[0] * sx + mvp[4] * sy + mvp[8] * sz + mvp[12]) / sw + 1) * 0.5 * canvasWidth;
                    const startScreenY = (1 - (mvp[1] * sx + mvp[5] * sy + mvp[9] * sz + mvp[13]) / sw) * 0.5 * canvasHeight;
                    const endScreenX = ((mvp[0] * ex + mvp[4] * ey + mvp[8] * ez + mvp[12]) / ew + 1) * 0.5 * canvasWidth;
                    const endScreenY = (1 - (mvp[1] * ex + mvp[5] * ey + mvp[9] * ez + mvp[13]) / ew) * 0.5 * canvasHeight;

                    const minX = Math.min(startScreenX, endScreenX);
                    const maxX = Math.max(startScreenX, endScreenX);
                    const minY = Math.min(startScreenY, endScreenY);
                    const maxY = Math.max(startScreenY, endScreenY);

                    // Ekran dışındaki edge'leri atla
                    if (maxX < 0 || minX > canvasWidth || maxY < 0 || minY > canvasHeight) continue;

                    const edge: IndexedEdge = {
                        objId,
                        edgeIdx: idx,
                        minX, minY, maxX, maxY,
                        depth
                    };

                    this.addEdgeToGrid(edge);
                    edgesThisFrame++;
                    this.totalEdgesIndexed++;
                }
            } else {
                // GPU başarısız oldu, CPU fallback
                edgesThisFrame += this.indexObjectCPU(ref, objId, canvasWidth, canvasHeight);
            }

            ref.lastIndexFrame = this.frameCounter;
        }

        return edgesThisFrame;
    }

    /**
     * CPU FALLBACK: Optimize edilmiş CPU path
     */
    private continueIndexingCPU(
        canvasWidth: number,
        canvasHeight: number,
        projMatrix: THREE.Matrix4,
        viewMatrix: THREE.Matrix4
    ): number {
        let edgesThisFrame = 0;

        while (this.pendingObjects.length > 0 && edgesThisFrame < this.maxEdgesPerFrame) {
            const ref = this.pendingObjects.shift()!;
            const objId = this.objectIdMap.get(ref.object.uuid);
            if (objId === undefined) continue;

            // World matrix güncelle
            ref.object.updateMatrixWorld(true);
            ref.worldMatrix.copy(ref.object.matrixWorld);

            // MVP = P * V * W
            this._mvpMatrix.multiplyMatrices(projMatrix, viewMatrix);
            this._mvpMatrix.multiply(ref.worldMatrix);

            edgesThisFrame += this.indexObjectCPU(ref, objId, canvasWidth, canvasHeight);
            ref.lastIndexFrame = this.frameCounter;
        }

        return edgesThisFrame;
    }

    /**
     * Tek objenin edge'lerini CPU'da indexle
     */
    private indexObjectCPU(ref: EdgeObjectRef, objId: number, canvasWidth: number, canvasHeight: number): number {
        const mvp = this._mvpMatrix.elements;
        const posArray = ref.positionArray;
        const edgeCount = ref.edgeCount;
        let indexed = 0;

        // Her edge için - zero allocation loop
        for (let i = 0; i < edgeCount && indexed < this.maxEdgesPerFrame; i++) {
            const idx = i * 2;

            // Start point
            const sx = posArray[idx * 3], sy = posArray[idx * 3 + 1], sz = posArray[idx * 3 + 2];
            // End point
            const ex = posArray[(idx + 1) * 3], ey = posArray[(idx + 1) * 3 + 1], ez = posArray[(idx + 1) * 3 + 2];

            // MVP transform (inline)
            const sw = mvp[3] * sx + mvp[7] * sy + mvp[11] * sz + mvp[15];
            const ew = mvp[3] * ex + mvp[7] * ey + mvp[11] * ez + mvp[15];

            // Kamera arkasındaki edge'leri atla
            if (sw <= 0.001 && ew <= 0.001) continue;

            // Screen coordinates
            const startScreenX = ((mvp[0] * sx + mvp[4] * sy + mvp[8] * sz + mvp[12]) / sw + 1) * 0.5 * canvasWidth;
            const startScreenY = (1 - (mvp[1] * sx + mvp[5] * sy + mvp[9] * sz + mvp[13]) / sw) * 0.5 * canvasHeight;
            const endScreenX = ((mvp[0] * ex + mvp[4] * ey + mvp[8] * ez + mvp[12]) / ew + 1) * 0.5 * canvasWidth;
            const endScreenY = (1 - (mvp[1] * ex + mvp[5] * ey + mvp[9] * ez + mvp[13]) / ew) * 0.5 * canvasHeight;

            // AABB hesapla
            const minX = Math.min(startScreenX, endScreenX);
            const maxX = Math.max(startScreenX, endScreenX);
            const minY = Math.min(startScreenY, endScreenY);
            const maxY = Math.max(startScreenY, endScreenY);

            // Ekran dışındaki edge'leri atla
            if (maxX < 0 || minX > canvasWidth || maxY < 0 || minY > canvasHeight) continue;

            // Derinlik (z-sorting için)
            const depth = (sw + ew) * 0.5;

            // Edge verisi
            const edge: IndexedEdge = {
                objId,
                edgeIdx: idx,
                minX, minY, maxX, maxY,
                depth
            };

            // Grid hücrelerine ekle
            this.addEdgeToGrid(edge);

            indexed++;
            this.totalEdgesIndexed++;
        }

        return indexed;
    }

    /**
     * Edge'i ilgili grid hücrelerine ekle
     */
    private addEdgeToGrid(edge: IndexedEdge): void {
        const cellMinX = Math.floor(edge.minX / this.cellSize);
        const cellMaxX = Math.floor(edge.maxX / this.cellSize);
        const cellMinY = Math.floor(edge.minY / this.cellSize);
        const cellMaxY = Math.floor(edge.maxY / this.cellSize);

        // Edge'in geçtiği tüm hücrelere ekle
        for (let cx = cellMinX; cx <= cellMaxX; cx++) {
            for (let cy = cellMinY; cy <= cellMaxY; cy++) {
                const key = `${cx},${cy}`;
                let cell = this.grid.get(key);
                if (!cell) {
                    cell = { edges: [] };
                    this.grid.set(key, cell);
                }
                cell.edges.push(edge);
            }
        }
    }

    /**
     * 🎯 MOUSE POZİSYONUNA EN YAKIN EDGE'İ BUL
     * O(1) grid lookup + sadece ilgili hücrelerdeki edge'leri test et
     */
    findClosestEdge(
        mousePixelX: number,
        mousePixelY: number,
        camera: THREE.Camera,
        canvasWidth: number,
        canvasHeight: number,
        pixelThreshold: number = 30
    ): {
        objectRef: EdgeObjectRef;
        edgeIndex: number;
        pixelDistance: number;
        closestPoint: THREE.Vector3;
        edgeStart: THREE.Vector3;
        edgeEnd: THREE.Vector3;
    } | null {
        const startTime = performance.now();

        // Index hazır değilse null dön
        if (!this.isIndexValid && !this.isIndexing) {
            return null;
        }

        // Mouse etrafındaki grid hücrelerini bul
        const searchRadius = pixelThreshold + this.cellSize; // Biraz fazla ara
        const cellMinX = Math.floor((mousePixelX - searchRadius) / this.cellSize);
        const cellMaxX = Math.floor((mousePixelX + searchRadius) / this.cellSize);
        const cellMinY = Math.floor((mousePixelY - searchRadius) / this.cellSize);
        const cellMaxY = Math.floor((mousePixelY + searchRadius) / this.cellSize);

        // Aday edge'leri topla - ZERO-ALLOC: Numeric key ile dedup (string allocation yok)
        const candidateSet = new Set<number>();
        const candidates: IndexedEdge[] = [];

        for (let cx = cellMinX; cx <= cellMaxX; cx++) {
            for (let cy = cellMinY; cy <= cellMaxY; cy++) {
                const cell = this.grid.get(`${cx},${cy}`);
                if (!cell) continue;

                for (let _ci = 0, _clen = cell.edges.length; _ci < _clen; _ci++) {
                    const edge = cell.edges[_ci];
                    // Numeric key: objId * 1000000 + edgeIdx (allocation-free dedup)
                    const key = edge.objId * 1000000 + edge.edgeIdx;
                    if (!candidateSet.has(key)) {
                        candidateSet.add(key);

                        // SCREEN-SPACE AABB EARLY-EXIT: Edge AABB mouse'a cok uzaksa atla
                        const aabbCenterX = (edge.minX + edge.maxX) * 0.5;
                        const aabbCenterY = (edge.minY + edge.maxY) * 0.5;
                        const aabbHalfW = (edge.maxX - edge.minX) * 0.5;
                        const aabbHalfH = (edge.maxY - edge.minY) * 0.5;
                        const adx = Math.abs(mousePixelX - aabbCenterX) - aabbHalfW;
                        const ady = Math.abs(mousePixelY - aabbCenterY) - aabbHalfH;
                        const aabbDist = Math.max(adx, 0) * Math.max(adx, 0) + Math.max(ady, 0) * Math.max(ady, 0);
                        // 2x threshold ile erken cikis (kesin disardakiler)
                        if (aabbDist > pixelThreshold * pixelThreshold * 4) continue;

                        candidates.push(edge);
                    }
                }
            }
        }

        this.stats.lastQueryCandidates = candidates.length;

        if (candidates.length === 0) {
            this.stats.lastQueryTime = performance.now() - startTime;
            return null;
        }

        // Depth'e göre sırala (yakın önce)
        candidates.sort((a, b) => a.depth - b.depth);

        // En yakın edge'i bul
        let bestEdge: IndexedEdge | null = null;
        let bestDistance = pixelThreshold;
        let bestT = 0;

        // MVP matrix (screen projection için)
        const projMatrix = (camera as THREE.PerspectiveCamera).projectionMatrix;
        const viewMatrix = camera.matrixWorldInverse;

        for (const edge of candidates) {
            const ref = this.edgeObjects[edge.objId];
            if (!ref || !ref.object.visible) continue;

            // World matrix güncelle (animasyon varsa)
            ref.object.updateMatrixWorld(false);

            // MVP = P * V * W
            this._mvpMatrix.multiplyMatrices(projMatrix, viewMatrix);
            this._mvpMatrix.multiply(ref.object.matrixWorld);
            const mvp = this._mvpMatrix.elements;

            const posArray = ref.positionArray;
            const idx = edge.edgeIdx;

            // Start/End points
            const sx = posArray[idx * 3], sy = posArray[idx * 3 + 1], sz = posArray[idx * 3 + 2];
            const ex = posArray[(idx + 1) * 3], ey = posArray[(idx + 1) * 3 + 1], ez = posArray[(idx + 1) * 3 + 2];

            // Screen coordinates
            const sw = mvp[3] * sx + mvp[7] * sy + mvp[11] * sz + mvp[15];
            const ew = mvp[3] * ex + mvp[7] * ey + mvp[11] * ez + mvp[15];

            if (sw <= 0.001 && ew <= 0.001) continue;

            const startScreenX = ((mvp[0] * sx + mvp[4] * sy + mvp[8] * sz + mvp[12]) / sw + 1) * 0.5 * canvasWidth;
            const startScreenY = (1 - (mvp[1] * sx + mvp[5] * sy + mvp[9] * sz + mvp[13]) / sw) * 0.5 * canvasHeight;
            const endScreenX = ((mvp[0] * ex + mvp[4] * ey + mvp[8] * ez + mvp[12]) / ew + 1) * 0.5 * canvasWidth;
            const endScreenY = (1 - (mvp[1] * ex + mvp[5] * ey + mvp[9] * ez + mvp[13]) / ew) * 0.5 * canvasHeight;

            // Point to line segment distance
            const abX = endScreenX - startScreenX;
            const abY = endScreenY - startScreenY;
            const apX = mousePixelX - startScreenX;
            const apY = mousePixelY - startScreenY;
            const abLen2 = abX * abX + abY * abY;

            let t = 0;
            if (abLen2 > 0.0001) {
                t = Math.max(0, Math.min(1, (apX * abX + apY * abY) / abLen2));
            }

            const closestX = startScreenX + t * abX;
            const closestY = startScreenY + t * abY;
            const dx = mousePixelX - closestX;
            const dy = mousePixelY - closestY;
            // PERFORMANS: Squared distance kullan - Math.sqrt sadece sonucta
            const distanceSq = dx * dx + dy * dy;

            if (distanceSq < bestDistance * bestDistance) {
                bestDistance = Math.sqrt(distanceSq);
                bestEdge = edge;
                bestT = t;

                // ERKEN CIKIS: 3px altindaysa hemen dur
                if (bestDistance < 3) break;
            }
        }

        this.stats.lastQueryTime = performance.now() - startTime;

        if (!bestEdge) return null;

        // Sonuç için 3D koordinatları hesapla
        const ref = this.edgeObjects[bestEdge.objId];
        const posArray = ref.positionArray;
        const idx = bestEdge.edgeIdx;
        const wm = ref.object.matrixWorld.elements;

        // Local coordinates
        const sx = posArray[idx * 3], sy = posArray[idx * 3 + 1], sz = posArray[idx * 3 + 2];
        const ex = posArray[(idx + 1) * 3], ey = posArray[(idx + 1) * 3 + 1], ez = posArray[(idx + 1) * 3 + 2];

        // World transform
        const edgeStart = new THREE.Vector3(
            wm[0] * sx + wm[4] * sy + wm[8] * sz + wm[12],
            wm[1] * sx + wm[5] * sy + wm[9] * sz + wm[13],
            wm[2] * sx + wm[6] * sy + wm[10] * sz + wm[14]
        );
        const edgeEnd = new THREE.Vector3(
            wm[0] * ex + wm[4] * ey + wm[8] * ez + wm[12],
            wm[1] * ex + wm[5] * ey + wm[9] * ez + wm[13],
            wm[2] * ex + wm[6] * ey + wm[10] * ez + wm[14]
        );
        const closestPoint = new THREE.Vector3().lerpVectors(edgeStart, edgeEnd, bestT);

        return {
            objectRef: ref,
            edgeIndex: bestEdge.edgeIdx,
            pixelDistance: bestDistance,
            closestPoint,
            edgeStart,
            edgeEnd
        };
    }

    /**
     * Index'i geçersiz kıl (obje değiştiğinde)
     */
    invalidateIndex(): void {
        this.isIndexValid = false;
        this.isIndexing = false;
        this.grid.clear();
        this.pendingObjects = [];
    }

    /**
     * Belirli bir objenin index'ini güncelle
     */
    invalidateObject(uuid: string): void {
        const objId = this.objectIdMap.get(uuid);
        if (objId !== undefined) {
            // Bu objenin edge'lerini grid'den çıkar (pahalı operasyon)
            // Daha iyi: full rebuild tetikle
            this.invalidateIndex();
        }
    }

    /**
     * Temizlik
     */
    dispose(): void {
        this.grid.clear();
        this.edgeObjects = [];
        this.objectIdMap.clear();
        this.pendingObjects = [];
    }

    /**
     * Debug stats
     */
    getStats(): typeof this.stats & {
        isValid: boolean;
        isIndexing: boolean;
        progress: number;
        objectCount: number;
    } {
        return {
            ...this.stats,
            isValid: this.isIndexValid,
            isIndexing: this.isIndexing,
            progress: this.indexingProgress,
            objectCount: this.edgeObjects.length
        };
    }

    /**
     * Grid hücre boyutunu ayarla
     */
    setCellSize(size: number): void {
        if (size !== this.cellSize) {
            this.cellSize = Math.max(10, Math.min(200, size)); // 10-200 arası
            this.invalidateIndex();
        }
    }

    /**
     * Frame başına max edge sayısını ayarla
     */
    setMaxEdgesPerFrame(count: number): void {
        this.maxEdgesPerFrame = Math.max(1000, count);
    }
}

/**
 * 🔷 TESSELLATED EDGE OPTIMIZER
 * Tessellation sonrası artan segment sayısını yönetir.
 *
 * Tessellated eğrisel kenar: 1 orijinal kenar → N segment (N = 8-64)
 * 1000 eğrisel kenar × 32 segment = 32000 GPU edge
 *
 * Optimizasyon stratejileri:
 * 1. Adaptif cell boyutu: Daha fazla edge = daha büyük cell (daha az grid hücresi)
 * 2. LOD entegrasyonu: Uzak tessellated kenarlar için segment skip
 * 3. Progressive batch: Frame'e yayarak index güncelleme
 * 4. Camera distance tabanlı tessellation LOD
 */
export class FikoTessellatedEdgeOptimizer {
    private lodManager: FikoEdgeLODManager;

    // Tessellation istatistikleri
    private stats = {
        totalOriginalEdges: 0,
        totalTessellatedSegments: 0,
        avgTessellationFactor: 0,
        lodSkippedSegments: 0,
        adaptiveCellSize: 50,
        lastOptimizeTime: 0
    };

    constructor() {
        this.lodManager = new FikoEdgeLODManager();
    }

    /**
     * Tessellation faktörüne göre adaptif cell boyutu hesapla
     *
     * Mantık: Daha fazla segment = daha büyük cell
     * - < 10K segment: 50px (varsayılan)
     * - 10K-50K: 75px
     * - 50K-100K: 100px
     * - > 100K: 150px
     *
     * Daha büyük cell = daha az grid hücresi = daha hızlı lookup
     * Ama daha fazla false positive (daha fazla candidate)
     * Trade-off: 32K segment için 75px optimal
     */
    calculateAdaptiveCellSize(totalSegments: number): number {
        if (totalSegments < 10000) return 50;
        if (totalSegments < 50000) return 75;
        if (totalSegments < 100000) return 100;
        return 150;
    }

    /**
     * Tessellated edge'ler için LOD-aware segment skip
     *
     * Uzak objelerdeki tessellated segment'lerin tamamını indexlemek yerine
     * her N. segment'i indexle.
     *
     * @param objectDistance Kamera mesafesi
     * @param segmentIndex Segment index (orijinal kenar içinde)
     * @param totalSegments O kenarın toplam segment sayısı
     * @returns true ise bu segment skip edilmeli
     */
    shouldSkipTessellatedSegment(
        objectDistance: number,
        segmentIndex: number,
        totalSegments: number
    ): boolean {
        // Yakın objeler: tüm segmentleri göster
        if (objectDistance < 200) return false;

        // Orta mesafe: her 2. segment (çok az kalite kaybı)
        if (objectDistance < 500) {
            return totalSegments > 16 && (segmentIndex % 2) !== 0;
        }

        // Uzak: her 4. segment
        if (objectDistance < 1000) {
            return totalSegments > 8 && (segmentIndex % 4) !== 0;
        }

        // Çok uzak: her 8. segment (minimal kalite kaybı, büyük performans kazancı)
        return totalSegments > 4 && (segmentIndex % 8) !== 0;
    }

    /**
     * Spatial index için optimal maxEdgesPerFrame hesapla
     *
     * Tessellated edge'ler daha fazla segment ürettiği için
     * frame başına daha fazla edge indexlemek gerekir.
     */
    calculateMaxEdgesPerFrame(totalSegments: number, targetFrameMs: number = 4): number {
        // Hedef: 4ms per frame (16ms budget'ın %25'i)
        // Benchmark: ~100K edge/ms CPU, ~1M edge/ms GPU
        // Conservative: 50K/frame default → tessellation durumunda artır

        if (totalSegments < 50000) return 50000;
        if (totalSegments < 100000) return 100000; // Tek frame'de bitirebilir
        return 200000; // 2-3 frame'de bitir
    }

    /**
     * Tessellated edge verilerini analiz et
     */
    analyzeTessellation(edgeObjects: Array<{ edgeCount: number; object: any }>): void {
        let totalSegments = 0;
        let originalEdges = 0;

        for (const ref of edgeObjects) {
            totalSegments += ref.edgeCount;
            // Heuristik: Eğer edgeCount > 100 ise muhtemelen tessellated
            // Orijinal kenar sayısı ≈ edgeCount / avg_tessellation_factor
            if (ref.edgeCount > 100) {
                originalEdges += Math.ceil(ref.edgeCount / 16); // Tahmini 16 segment/kenar
            } else {
                originalEdges += ref.edgeCount;
            }
        }

        this.stats.totalTessellatedSegments = totalSegments;
        this.stats.totalOriginalEdges = originalEdges;
        this.stats.avgTessellationFactor = originalEdges > 0 ? totalSegments / originalEdges : 1;
        this.stats.adaptiveCellSize = this.calculateAdaptiveCellSize(totalSegments);
    }

    getStats(): typeof this.stats {
        return { ...this.stats };
    }
}

/**
 * 🔷 EDGE LOD MANAGER
 * Uzak objeler için edge basitleştirme
 */
export class FikoEdgeLODManager {
    private lodLevels: Map<string, number> = new Map(); // UUID -> LOD level

    // LOD mesafe eşikleri (kamera uzaklığı)
    private readonly LOD_THRESHOLDS = [
        { distance: 100, skipRatio: 1 },    // Yakın: Tüm edge'ler
        { distance: 500, skipRatio: 2 },    // Orta: Her 2. edge
        { distance: 1000, skipRatio: 4 },   // Uzak: Her 4. edge
        { distance: 2000, skipRatio: 8 },   // Çok uzak: Her 8. edge
        { distance: Infinity, skipRatio: 16 } // En uzak: Her 16. edge
    ];

    /**
     * Obje için LOD seviyesini hesapla
     */
    calculateLOD(objectDistance: number): number {
        for (let i = 0; i < this.LOD_THRESHOLDS.length; i++) {
            if (objectDistance < this.LOD_THRESHOLDS[i].distance) {
                return this.LOD_THRESHOLDS[i].skipRatio;
            }
        }
        return 16;
    }

    /**
     * Edge'in LOD'a göre atlanıp atlanmayacağını kontrol et
     */
    shouldSkipEdge(edgeIndex: number, lodLevel: number): boolean {
        if (lodLevel <= 1) return false;
        return (edgeIndex % lodLevel) !== 0;
    }

    /**
     * Tüm LOD seviyelerini temizle
     */
    clear(): void {
        this.lodLevels.clear();
    }
}
